<template>
  <v-dialog v-model="dialog" persistent max-width="520px">
    <v-card class="person-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">New Delivery Person</div>
          <div class="dialog-subtitle">
            Add someone who can scan incoming warehouse inventory.
          </div>
        </div>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <div class="field-label">Name</div>
        <v-text-field
          ref="name"
          v-model.trim="person.name"
          label="Name"
          outlined
          dense
          autofocus
          :error-messages="nameErrors"
          @keyup.enter="submit"
        />

        <div class="field-label">Description</div>
        <v-textarea
          v-model.trim="person.note"
          label="Note (optional)"
          outlined
          dense
          rows="2"
        />

        <div class="access-box">
          <div class="access-heading">Warehouse access code</div>
          <div class="access-help">
            This private code identifies the delivery person on the mobile page.
          </div>
          <div class="access-row mt-3">
            <v-text-field
              v-model="person.accessCode"
              label="Access code"
              outlined
              dense
              readonly
              hide-details
            />
            <v-btn outlined color="primary" @click="regenerate">
              <v-icon left>mdi-refresh</v-icon>
              Generate
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn text color="primary" @click="close">Close</v-btn>
        <v-btn
          color="primary"
          depressed
          rounded
          dark
          :loading="loading"
          @click="submit"
        >
          Save Person
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import service from "../service";
const { validationMixin } = require("vuelidate");
const { required, minLength } = require("vuelidate/lib/validators");

export default {
  name: "CreateInboundDeliveryPerson",
  mixins: [validationMixin],
  props: {
    createDialog: Boolean,
  },
  data() {
    return {
      dialog: false,
      loading: false,
      person: this.emptyPerson(),
    };
  },
  validations: {
    person: {
      name: { required, minLength: minLength(2) },
      accessCode: { required },
    },
  },
  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.person.name.$dirty) return errors;
      if (!this.$v.person.name.required) errors.push("Name is required.");
      if (!this.$v.person.name.minLength)
        errors.push("Name must contain at least 2 characters.");
      return errors;
    },
  },
  watch: {
    createDialog(value) {
      this.dialog = value;
      if (value) {
        this.person = this.emptyPerson();
        this.$v.$reset();
        this.$nextTick(() => this.$refs.name && this.$refs.name.focus());
      }
    },
  },
  methods: {
    emptyPerson() {
      return {
        name: "",
        note: "",
        active: true,
        accessCode: service.generateAccessCode(),
      };
    },
    regenerate() {
      this.person.accessCode = service.generateAccessCode();
    },
    close() {
      if (this.loading) return;
      this.dialog = false;
      this.$emit("close");
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.loading = true;
      try {
        await service.create(this.person);
        this.loading = false;
        this.$emit("submit");
        this.close();
        this.$swal("Saved", "Delivery person has been created.", "success");
      } catch (error) {
        const message =
          (error.data && error.data.message) || "Unable to create delivery person.";
        this.$swal("Delivery Person", message, "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.person-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}
.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.dialog-title {
  color: #0f172a;
  font-size: 20px;
  font-weight: 800;
}
.dialog-subtitle {
  color: #64748b;
  font-size: 13px;
  margin-top: 2px;
}
.field-label {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}
.dialog-actions {
  padding: 12px 16px;
}
.access-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
}
.access-heading {
  color: #0f172a;
  font-weight: 800;
}
.access-help {
  color: #64748b;
  font-size: 12px;
  margin-top: 3px;
}
.access-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
@media (max-width: 600px) {
  .access-row {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
